import { ArrowLeft, ArrowRight, Check } from '@timelyapp/icons/solid-icons';
import React from 'react';
import Button, { type ButtonProps } from '../Button';
import { Box } from '../box/Box';
import { Circle } from '../circle/Circle';
import { Spinner } from '../spinner/Spinner';
import { Text } from '../text/Text';
import type { UseWizardResult, WizardStepComponentProps } from './wizard.types';

// Shared function to process step status and circle props
function getStepState(step: any, index: number, stepStates: any, currentIndex: number) {
  const stepStatus = stepStates[step.id]?.status || 'pending';
  const circleStatus =
    stepStatus === 'error' ? 'error' : stepStatus === 'loading' ? 'loading' : undefined;
  const disabled = index > currentIndex;

  return {
    stepStatus,
    circleStatus,
    disabled,
    circleVariant: stepStatus === 'pending' ? 'outline' : 'solid',
    circleContent: stepStatus === 'completed' ? <Check width={16} /> : index + 1,
    textColor: ['completed', 'current'].includes(stepStatus) ? 'normal' : 'minContrast',
  } as const;
}

// Navigation button props
type WizardNavigation = UseWizardResult['navigation'];
interface NavButtonProps extends Omit<ButtonProps, 'isLoading'>, WizardNavigation {}

// Navigation buttons
const Nav = {
  Back: ({
    isFirstStep,
    isLastStep,
    isLoading,
    hasError,
    onClick,
    children = 'Back',
    setLoading,
    next,
    prev,
    complete,
    skip,
    goTo,
    ...props
  }: NavButtonProps) => (
    <Button
      disabled={isFirstStep || isLoading || hasError}
      onClick={onClick}
      priority="xlow"
      {...props}
    >
      <ArrowLeft />
      {children}
    </Button>
  ),

  Skip: ({
    isFirstStep,
    isLastStep,
    isLoading,
    hasError,
    onClick,
    children = 'Skip',
    setLoading,
    next,
    prev,
    complete,
    skip,
    goTo,
    ...props
  }: NavButtonProps) => (
    <Button
      disabled={isLastStep || isLoading || hasError}
      onClick={onClick}
      priority="xlow"
      {...props}
    >
      {children}
    </Button>
  ),

  Next: ({
    isLastStep,
    isFirstStep,
    isLoading,
    hasError,
    onClick,
    children,
    setLoading,
    next,
    prev,
    complete,
    skip,
    goTo,
    ...props
  }: NavButtonProps) => (
    <>
      {isLoading && <Spinner />}
      <Button onClick={onClick} disabled={hasError || isLoading} priority="xhigh" {...props}>
        {children || (isLastStep ? 'Complete' : 'Next step')}
        {!isLoading && <>{isLastStep ? <Check /> : <ArrowRight />}</>}
      </Button>
    </>
  ),
};

const Root = ({ children, ...props }: BoxProps) => (
  <Box.Column width="100%" {...props}>
    {children}
  </Box.Column>
);

export interface BoxProps extends React.ComponentProps<typeof Box.Column> {}

// Wizard Component
export const Wizard = Object.assign(Root, {
  Root,
  /**
   * Progress bar with step indicators
   *
   * Renders step indicators in a horizontal layout by wrapping them in a {@link Box.Row} component.
   * Each step shows its current status (pending, completed, error, loading) with visual indicators.
   *
   * @extends BoxProps
   */
  Progress: ({ wizard, ...rest }: WizardStepComponentProps<any> & Omit<BoxProps, 'children'>) => {
    const { steps, currentId, stepStates } = wizard;

    // Handle empty steps array
    if (steps.length === 0) {
      return null;
    }

    const currentIndex = steps.findIndex(s => s.id === currentId);
    return (
      <Box.Row gap="large" justifyContent="space-between" width="100%" {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(step, index, stepStates, currentIndex);
          const lastStep = index === steps.length - 1;
          const hasStepTitle = !!step.title;
          const separatorStyle = hasStepTitle ? { marginTop: -26 } : { width: 0 };

          return (
            <React.Fragment key={step.id}>
              <Box.Column alignContent="center" alignItems="center" gap="large">
                <Circle
                  layout="step"
                  key={step.id}
                  status={state.circleStatus}
                  // onClick={() => !state.disabled && navigation.goTo(step.id)}
                  variant={state.circleVariant}
                >
                  {state.circleContent}
                </Circle>
                {!!step.title && (
                  <Text size="xsmall" color={state.textColor}>
                    {step.title}
                  </Text>
                )}
              </Box.Column>
              {!lastStep && <Box.Separator style={separatorStyle} />}
            </React.Fragment>
          );
        })}
      </Box.Row>
    );
  },

  /**
   * Vertical progress list with step indicators
   *
   * Renders step indicators in a vertical list layout optimized for sidebars.
   * Each step shows its current status (pending, completed, error, loading) with visual indicators.
   * Uses the same step logic as Progress but with vertical list layout.
   *
   * @extends BoxProps
   */
  ProgressList: ({
    wizard,
    ...rest
  }: WizardStepComponentProps<any> & Omit<BoxProps, 'children'>) => {
    const { steps, currentId, stepStates } = wizard;

    // Handle empty steps array
    if (steps.length === 0) {
      return null;
    }

    const currentIndex = steps.findIndex(s => s.id === currentId);
    return (
      <Box.Column {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(step, index, stepStates, currentIndex);
          const lastStep = index === steps.length - 1;
          return (
            <React.Fragment key={step.id}>
              <Box.Row gap="large" alignItems="center">
                <Circle
                  layout="wizard"
                  status={state.circleStatus}
                  variant={state.circleVariant}
                  // onClick={() => !state.disabled && navigation.goTo(step.id)}
                >
                  {state.circleContent}
                </Circle>
                {step.title && <Text color={state.textColor}>{step.title}</Text>}
              </Box.Row>

              {!lastStep && (
                <Box.Separator
                  orientation="vertical"
                  style={{ alignSelf: 'flex-start', marginLeft: 4 * 4, minHeight: 4 * 4 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box.Column>
    );
  },

  // Container for step content
  Content: ({ children, ...rest }: BoxProps) => (
    <Box.Column gap="medium" {...rest}>
      {children}
    </Box.Column>
  ),

  // Navigation controls
  Nav: {
    Root: ({ children, ...rest }: BoxProps) => (
      <Box.Row justifyContent="space-between" width="100%" gap="large" {...rest}>
        {children}
      </Box.Row>
    ),
    Back: Nav.Back,
    Skip: Nav.Skip,
    Next: Nav.Next,
  },
});
